import React, { useState, useEffect, useRef, useCallback } from "react";
import LocationAutocomplete from "./components/OpenStreetAutocomplete";
import ExpandedJobseekerView from "./components/ExpandJobseeker";
import { API_BASE_URL } from "./utils/api";
import { sortedJobseekers, getNameInitials, capitalizeWords } from "./utils";
import { Tooltip as ToolTipHtml } from "react-tooltip";
import TagComponent from "./components/Tags";
import { useTag } from "./services/Tags";
import JobseekerService from "./services/JobseekerService";
import MessageInterface from "./MessageInterface";
import Modal from "react-modal";

// Updated SearchJobseekers component with location autocomplete
const SearchJobseekers = ({ user }) => {
  const [searchFilters, setSearchFilters] = useState({
    keyword: "",
    jobRole: "",
    location: "",
    locationData: null, // Store full location data including lat/lng
    radius: "",
    user: user.id,
    company: user.company,
  });
  const [jobseekers, setJobseekers] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const autocompleteRef = useRef();
  const [jobseekerAverageScore, setJobseekerAverageScore] = useState([]);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [selectedJobseekers, setSelectedJobseekers] = useState([]);
  const [expandedJobseeker, setExpandedJobseeker] = useState(null);
  const { deleteJobseekerTag, getTagsByCompany } = useTag();
  const [selectAll, setSelectAll] = useState(false);

  // New state for pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 20,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const handleCloseModal = () => {
    setModalState({ isOpen: false, type: null, jobSeekerId: null });
  };

  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    //jobSeekerId: null,
  });

  const handleInputChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLocationSelect = useCallback((locationData) => {
    setSearchFilters((prev) => ({
      ...prev,
      location: locationData.address,
      locationData: locationData,
    }));
  }, []);

  // Updated handleSearch to support pagination
  const handleSearch = async (page = 1, append = false) => {
    try {
      setIsLoading(!append);
      setIsLoadingMore(append);

      const searchParams = {
        ...searchFilters,
        page: page,
        limit: pagination.limit,
      };

      const response = await JobseekerService.searchJobseeker(searchParams);

      if (response.jobseekers) {
        const sortedData = await sortedJobseekers(response.jobseekers);

        if (append) {
          setJobseekers((prev) => [...prev, ...sortedData]);
        } else {
          setJobseekers(sortedData);
        }
        setPagination(response.pagination);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Handle load more functionality
  const handleLoadMore = () => {
    if (pagination.hasNextPage && !isLoadingMore) {
      handleSearch(pagination.currentPage + 1, true);
    }
  };

  // Handle Enter key press for search - modified to exclude location input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Separate handler for location input key events
  const handleLocationKeyPress = (e) => {
    // Only trigger search on Enter, don't interfere with autocomplete
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleReset = () => {
    setSearchFilters({
      keyword: "",
      jobRole: "",
      location: "",
      locationData: null,
      radius: "",
      user: user.id,
      company: user.company,
    });

    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      hasNextPage: false,
      hasPrevPage: false,
      limit: 20,
    });

    autocompleteRef.current.reset();
    setJobseekers([]);
  };

  const handleCheckboxChange = (jobseekerId, jobseekerName, jobseeker) => {
    setSelectedJobseekers((prev) => {
      // Check if the jobseeker is already in the array
      const existingIndex = prev.findIndex((js) => js.id === jobseekerId);

      if (existingIndex !== -1) {
        // If found, remove the jobseeker
        return prev.filter((js) => js.id !== jobseekerId);
      } else {
        // If not found, add the jobseeker
        return [
          ...prev,
          { id: jobseekerId, name: jobseekerName, jobseeker: jobseeker },
        ];
      }
    });
  };

  const getStatusColor = (status) => {
    if (!status) return "";
    switch (status.toLowerCase()) {
      case "not_started":
        return "bg-gray-200 text-gray-800";
      case "in_progress":
        return "bg-yellow-200 text-yellow-800";
      case "completed":
        return "bg-green-200 text-green-800";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const getApplicationStatusColor = (status) => {
    if (!status) return "";
    switch (status.toLowerCase()) {
      case "applied":
        return "bg-blue-200 text-blue-800";
      case "interview":
        return "bg-purple-200 text-purple-800";
      case "hired":
        return "bg-green-200 text-green-800";
      case "rejected":
        return "bg-red-200 text-red-800";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "border-green-500 text-green-700";
    if (score >= 60) return "border-yellow-500 text-yellow-700";
    return "border-red-500 text-red-700";
  };

  const removeTag = (jobseekerId, tagId) => {
    const jobseekerList = jobseekers.map((j) => {
      if (j._id === jobseekerId) {
        const f_tags = j.tags.filter((tag) => {
          return tag._id === tagId ? false : true;
        });
        j.tags = f_tags;
      }
      return j;
    });
    setJobseekers(jobseekerList);
  };

  const handleTagDelete = async (jobseekerId, tagId) => {
    await deleteJobseekerTag(tagId);
    removeTag(jobseekerId, tagId);
  };

  useEffect(() => {
    const getAllJobseekers = async () => {
      await handleSearch();
    };
    getAllJobseekers();
  }, []);

  const handleSelectAllChange = () => {
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (selectAll) {
      setSelectedJobseekers(
        jobseekers.map((js) => {
          return { id: js._id, name: js.name };
        }),
      );
    } else {
      setSelectedJobseekers([]);
    }
  }, [selectAll, jobseekers]);

  const handleSendMessages = () => {
    setModalState({ isOpen: true, type: null, jobSeekerId: null });
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-col lg:flex-row justify-between items-center mb-4">
        <h2 className="text-2xl font-bold mb-4 lg:mb-0">Search Jobseekers</h2>
      </div>

      <div className="flex flex-col space-y-4 w-full p-4">
  {/* All Search Inputs - Same Height and Width */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    {/* Search Keyword Input */}
    <div className="w-full">
      <input
        type="text"
        placeholder="Search by keyword..."
        value={searchFilters.keyword}
        onChange={(e) => handleInputChange("keyword", e.target.value)}
        onKeyUp={handleKeyPress}
        className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>

    {/* Job Role Input */}
    <div className="w-full">
      <input
        type="text"
        placeholder="Search by Role..."
        value={searchFilters.jobRole}
        onChange={(e) => handleInputChange("jobRole", e.target.value)}
        onKeyUp={handleKeyPress}
        className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>

    {/* Location Autocomplete */}
    <div className="w-full">
      <LocationAutocomplete
        onSelectLocation={handleLocationSelect}
        placeholder="Enter location..."
        ref={autocompleteRef}
        onKeyUp={handleLocationKeyPress}
      />
    </div>

    {/* Search Radius Select */}
    <div className="w-full">
      <select
        value={searchFilters.radius}
        onChange={(e) => handleInputChange("radius", e.target.value)}
        onKeyUp={handleKeyPress}
        className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
      >
        <option value="">Select Radius</option>
        <option value="5">5 km</option>
        <option value="10">10 km</option>
        <option value="15">15 km</option>
        <option value="20">20 km</option>
        <option value="25">25 km</option>
        <option value="50">50 km</option>
        <option value="100">100 km</option>
        <option value="2000">Unlimited</option>
      </select>
    </div>
  </div>

  {/* Centered Buttons and Info Row */}
  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
    {/* Action Buttons - Centered */}
    <div className="flex gap-3">
      <button
        onClick={() => handleSearch()}
        disabled={isLoading}
        className="px-6 py-2 h-10 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? "Searching..." : "Search"}
      </button>
      <button
        onClick={handleReset}
        disabled={isLoading}
        className="px-4 py-2 h-10 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Reset
      </button>
    </div>

    {/* Info Icon with Tooltip */}
    <div className="relative">
      <div
        className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center cursor-help"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <svg
          className="w-4 h-4 text-blue-600"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {showTooltip && (
        <div className="absolute right-0 bottom-12 sm:top-10 w-64 bg-gray-800 text-white text-sm p-3 rounded-lg shadow-lg z-10">
          <div className="absolute -bottom-2 sm:-top-2 right-4 w-0 h-0 border-l-4 border-r-4 sm:border-b-4 border-t-4 sm:border-t-0 border-transparent sm:border-b-gray-800 border-t-gray-800 sm:border-t-transparent"></div>
          <p className="font-semibold mb-1">Search Tips:</p>
          <ul className="text-xs space-y-1">
            <li>• Use keywords to find name, email</li>
            <li>• Select job role to filter by position</li>
            <li>• Type city name for autocomplete</li>
            <li>• Adjust radius to expand search area</li>
            <li>• Press Enter to search</li>
          </ul>
        </div>
      )}
    </div>
  </div>
</div>

      {/* Results Summary */}
      {pagination.totalCount > 0 && (
        <div className="mt-6 mb-4 text-sm text-gray-600">
          Showing {jobseekers.length} of {pagination.totalCount} jobseekers
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-center space-x-0 sm:space-x-4 space-y-2 sm:space-y-0 w-full justify-center lg:justify-between">
        <div
          className="flex items-center cursor-pointer w-auto"
          onClick={handleSelectAllChange}
        >
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAllChange}
            className="w-5 h-5 mr-2 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          />
          <label className="cursor-pointer">Select All</label>
        </div>
        <div className="flex items-center cursor-pointer w-auto">
          <button
            className={`px-6 py-2 ${selectedJobseekers.length > 0 ? `focus:ring-blue-500 bg-blue-600 text-white hover:bg-blue-700` : `bg-gray-200 text-gray-700 text-white`}  rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={handleSendMessages}
          >{`Send Messages to ${selectedJobseekers.length} Jobseeker(s)`}</button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden mt-10">
        {jobseekers.length === 0 && !isLoading ? (
          <div className="text-center py-8">
            <div className="text-gray-500 text-lg">No jobseekers found.</div>
            <div className="text-gray-400 text-sm mt-2">
              Try adjusting your search criteria
            </div>
          </div>
        ) : (
          ""
        )}

        {isLoading && jobseekers.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="text-gray-500 mt-2">Loading jobseekers...</div>
          </div>
        ) : (
          ""
        )}

        {jobseekers.map((jobseeker) => (
          <div
            key={jobseeker._id}
            className="border-b border-gray-200 last:border-b-0"
          >
            <div className="flex flex-col sm:flex-row items-center p-4 hover:bg-gray-50">
              <div className="mr-4" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedJobseekers.some(
                    (element) => element.id == jobseeker._id,
                  )}
                  onChange={() =>
                    handleCheckboxChange(
                      jobseeker._id,
                      jobseeker.name,
                      jobseeker,
                    )
                  }
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
              </div>

              {jobseeker.avatar ? (
                <img
                  src={`${API_BASE_URL}${jobseeker.avatar?.replace(/^\//, "")}`}
                  alt={jobseeker.name}
                  className="w-16 h-16 rounded-full mr-4 object-cover cursor-pointer"
                  onClick={() =>
                    setExpandedJobseeker(
                      expandedJobseeker === jobseeker._id
                        ? null
                        : jobseeker._id,
                    )
                  }
                />
              ) : (
                <span
                  className="w-10 h-10 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-sm mr-4 cursor-pointer"
                  onClick={() =>
                    setExpandedJobseeker(
                      expandedJobseeker === jobseeker._id
                        ? null
                        : jobseeker._id,
                    )
                  }
                >
                  {getNameInitials(jobseeker.name)}
                </span>
              )}
              <div
                className="flex-grow cursor-pointer text-center sm:text-left"
                onClick={() =>
                  setExpandedJobseeker(
                    expandedJobseeker === jobseeker._id ? null : jobseeker._id,
                  )
                }
              >
                <h3 className="font-semibold">
                  {capitalizeWords(jobseeker.name)}
                </h3>
                <p className="text-sm text-gray-500">
                  {jobseeker.jobTitle
                    ? `${jobseeker.jobTitle} • ${jobseeker.location}`
                    : " - "}
                </p>
                <div className="block mt-2">
                  {jobseeker.tags.map((tag) => (
                    <TagComponent
                      key={tag._id}
                      text={tag.tag.name}
                      onDelete={(e) => {
                        e.stopPropagation();
                        handleTagDelete(jobseeker._id, tag._id);
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center space-x-0 sm:space-x-4 space-y-2 sm:space-y-0 mt-2 sm:mt-0">
                <span
                  title="applied on"
                  className={`px-2 py-1 rounded-full text-xs w-24 text-center`}
                >
                  {new Date(jobseeker.createdAt).toLocaleDateString()}
                </span>
                <span
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseOut={() => setTooltipOpen(false)}
                  data-tooltip-id="questionnaireStatusTooltip"
                  data-tooltip-content={`Questionnaire: ${jobseeker.detailedInfo?.questionnaireResponsesCompletedBy || "Not completed"} (${jobseeker.questionnaireCompletionRate || 0}%)`}
                  className={`px-2 py-1 rounded-full text-xs ${jobseeker.questionnaireStatus ? getStatusColor(jobseeker.questionnaireStatus) : "bg-gray-200 text-gray-600"} w-24 text-center`}
                >
                  {jobseeker?.questionnaireStatus
                    .replace("_", " ")
                    .charAt(0)
                    .toUpperCase() +
                    jobseeker?.questionnaireStatus.replace("_", " ").slice(1)}
                </span>
                {jobseeker.questionnaireCompletionRate ? (
                  <ToolTipHtml
                    id="questionnaireStatusTooltip"
                    isOpen={tooltipOpen}
                  />
                ) : (
                  ""
                )}
                <span
                  className={`px-2 py-1 rounded-full text-xs ${jobseeker.applicationStatus ? getApplicationStatusColor(jobseeker.applicationStatus) : ""} w-24 text-center`}
                >
                  {jobseeker.applicationStatus.charAt(0).toUpperCase() +
                    jobseeker.applicationStatus.slice(1)}
                </span>
                <div
                  className={`w-10 h-10 rounded-full border-2 ${getScoreColor(jobseeker.score ? jobseeker.score : 0)} flex items-center justify-center font-semibold`}
                >
                  {jobseeker.score ?? 0}
                </div>
              </div>
            </div>
            {expandedJobseeker === jobseeker._id && (
              <ExpandedJobseekerView
                jobseeker={jobseeker}
                selectedJob={jobseeker.jobId}
                jobseekersAverageScore={jobseekerAverageScore}
                initialJobseekers={jobseekers}
                companyUser={user}
              />
            )}
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {pagination.hasNextPage && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingMore ? (
              <div className="flex items-center">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Loading More...
              </div>
            ) : (
              `Load More (${pagination.totalCount - jobseekers.length} remaining)`
            )}
          </button>
        </div>
      )}

      <Modal
        isOpen={modalState.isOpen}
        onRequestClose={handleCloseModal}
        contentLabel={`JobSeeker Action: ${modalState.type}`}
        className={`w-auto mx-auto mt-20 p-6 bg-white rounded-lg shadow-xl ${modalState.type === "message" ? "min-w-1/3" : ""}`}
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      >
        <MessageInterface
          worker={selectedJobseekers}
          companyUser={user}
          initialJobseekers={jobseekers}
          showPrevMessage={true}
          canSelectOtherJobseekers={false}
        />
      </Modal>
    </div>
  );
};

export default SearchJobseekers;
